import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { newBlankPoster } from "@/lib/posterTemplate";
import { upsertSplit } from "@/lib/splitPosters";
import { GroupSendDialog } from "@/components/GroupSendDialog";
import { LiveToggle } from "@/components/LiveToggle";
import { Poster } from "@/components/PosterCanvas";
import { SendPosterButton } from "@/components/SendPosterButton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Film, Plus, Pencil, Loader2, Images, Copy, CheckCircle2, Lock, Split } from "lucide-react";
import { ProtectedDelete, LiveBadge, useLiveStatus } from "@/components/ProtectedDelete";

export default function PosterList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const live = useLiveStatus();
  const [posters, setPosters] = useState(null);
  const [creating, setCreating] = useState(false);
  const [defaultTx, setDefaultTx] = useState("fade");
  const [, setAdding] = useState(null);
  const [inBanner, setInBanner] = useState({});

  const load = async () => {
    const [p, b] = await Promise.all([api.get("/posters"), api.get("/banners").catch(() => ({ data: [] }))]);
    setPosters(p.data);
    const ids = {};
    b.data.forEach((bn) => (bn.images || []).forEach((img) => {
      if (img.type === "poster" && img.poster_data?.id) ids[img.poster_data.id] = true;
    }));
    setInBanner(ids);
    api.get("/settings").then(({ data: s }) => setDefaultTx(s.default_transition || "fade")).catch(() => {});
  };
  useEffect(() => {
    load();
  }, []);

  const addToBanner = async (p) => {
    setAdding(p.id);
    try {
      const { data: banners } = await api.get("/banners");
      if (!banners.length) {
        toast.error("Créez d'abord un bandeau");
        return;
      }
      const target = banners.find((bn) => bn.id === live.banner_id) || banners[0];
      const dup = (target.images || []).some(
        (img) => img.type === "poster" && img.poster_data?.id === p.id
      );
      if (dup) {
        toast.warning(`« ${p.name} » est déjà présente dans le bandeau « ${target.name} »`);
        return;
      }
      const slide = {
        id: crypto.randomUUID(), type: "poster", poster_data: p, url: "",
        duration: 10, transition: defaultTx, order: target.images?.length || 0,
      };
      const images = [...(target.images || []), slide].map((img, i) => ({ ...img, order: i }));
      await api.put(`/banners/${target.id}`, {
        name: target.name, aspect_ratio: target.aspect_ratio, loop: target.loop, images,
      });
      toast.success(`Mise en ligne dans le bandeau « ${target.name} »`);
      setInBanner((prev) => ({ ...prev, [p.id]: true }));
      live.reload();
    } catch {
      toast.error("Échec de l'ajout au bandeau");
    } finally {
      setAdding(null);
    }
  };

  const fromReference = () => {
    const usc = (posters || []).filter((p) => p.layout === "usc");
    const ref = usc.find((p) => p.name.trim().toLowerCase() === "matchs du week-end") || usc.find((p) => !p.source_poster_id) || usc[0];
    if (!ref) return newBlankPoster();
    const base = copyPayload(ref);
    return {
      ...base, name: "Nouvelle affiche", source_poster_id: "", layout: "usc",
      top: { ...(ref.top || {}), rows: [] },
      columns: (ref.columns || []).map((c) => ({ ...c, rows: [] })),
      footer: [],
    };
  };

  const create = async () => {
    setCreating(true);
    try {
      const payload = fromReference();
      const { data } = await api.post("/posters", payload);
      toast.success("Affiche créée");
      navigate(`/poster/${data.id}`);
    } catch {
      toast.error("Échec de la création");
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/posters/${id}`);
      setPosters((p) => p.filter((x) => x.id !== id));
      toast.success("Affiche supprimée");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la suppression");
    }
  };

  const copyPayload = (p) => ({
    name: `${p.name} (copie)`,
    title: p.title,
    club_name: p.club_name,
    logo_url: p.logo_url || "",
    team_name: p.team_name,
    highlight_team: p.highlight_team,
    color_primary: p.color_primary,
    color_accent: p.color_accent,
    locked: false,
    columns: p.columns,
    footer: p.footer,
    highlight_first_team: p.highlight_first_team,
    show_scores: p.show_scores,
    background_url: p.background_url || "",
    layout: p.layout || "standard",
    top: p.top || {},
    band: p.band || {},
    show_columns: p.show_columns !== false,
    show_logo: p.show_logo !== false,
    show_footer: p.show_footer !== false,
    show_title: p.show_title !== false,
    gold_lines: p.gold_lines || [],
    source_poster_id: p.source_poster_id || "",
  });

  const duplicate = async (p) => {
    try {
      const { data } = await api.post("/posters", copyPayload(p));
      setPosters((list) => [data, ...list]);
      toast.success("Affiche dupliquée");
    } catch {
      toast.error("Échec de la duplication");
    }
  };

  const takeOffline = async (p) => {
    setAdding(p.id);
    try {
      const { data: banners } = await api.get("/banners");
      const target = banners.find((bn) => bn.id === live.banner_id);
      if (!target) return;
      const images = (target.images || []).filter((img) => !(img.type === "poster" && img.poster_data?.id === p.id));
      if (!images.length) return toast.error("Impossible : c'est la seule image du bandeau en ligne, la TV serait vide. Ajoutez d'abord une autre image ou affiche.");
      await api.put(`/banners/${target.id}`, {
        name: target.name, aspect_ratio: target.aspect_ratio, loop: target.loop, images: images.map((img, i) => ({ ...img, order: i })),
      });
      toast.success(`« ${p.name} » retirée du bandeau « ${target.name} »`);
      await live.reload();
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec de la mise hors ligne");
    } finally {
      setAdding(null);
    }
  };

  const [splitting, setSplitting] = useState(null);
  const split = async (p) => {
    setSplitting(p.id);
    try {
      const made = await upsertSplit(p);
      setPosters((list) => [...made, ...list.filter((x) => !made.some((m) => m.id === x.id))]);
      toast.success("Affiches « Académie » et « Seniors » prêtes — le bandeau n'a pas été modifié");
    } catch {
      toast.error("Échec de la séparation");
    } finally {
      setSplitting(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="grain-overlay pointer-events-none fixed inset-0 opacity-60" />
      <header className="sticky top-11 z-30 border-b border-border bg-background/80 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground">
              <Film className="h-5 w-5" />
            </div>
            <div>
              <h1 className="font-display text-xl font-extrabold tracking-tight">LuminaStream</h1>
              <p className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                Affiches
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <GroupSendDialog />
            <Button onClick={() => create()} disabled={creating} data-testid="create-poster-button">
              {creating ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
              Nouvelle affiche
            </Button>
          </div>
        </div>
      </header>

      <main className="relative z-10 mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">Mes affiches</h2>
          <p className="text-sm text-muted-foreground">Affiches de matchs entièrement personnalisables.</p>
        </div>

        {posters === null ? (
          <div className="flex items-center justify-center py-24 text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : posters.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-24 text-center">
            <Images className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="font-display text-lg font-semibold">Aucune affiche</p>
            <p className="mb-5 text-sm text-muted-foreground">Créez votre première affiche de matchs.</p>
            <Button onClick={() => create()} data-testid="empty-create-poster-button">
              <Plus className="mr-1 h-4 w-4" /> Créer une affiche
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posters.map((p, i) => (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="group overflow-hidden rounded-2xl border border-border bg-card shadow-lg transition-colors hover:border-primary/50"
                data-testid={`poster-card-${p.id}`}
              >
                <div className="relative aspect-video w-full overflow-hidden bg-black">
                  <Poster data={p} className="h-full w-full" />
                  {inBanner[p.id] && (
                    <Badge
                      className="absolute right-2 top-2 z-10 gap-1 border-0 bg-emerald-600 text-white shadow-lg"
                      data-testid={`in-banner-badge-${p.id}`}
                    >
                      <CheckCircle2 className="h-3 w-3" /> Déjà dans le bandeau
                    </Badge>
                  )}
                  {live.poster_ids.includes(p.id) && (
                    <div className="absolute bottom-2 right-2 z-10"><LiveBadge testId={`live-badge-poster-${p.id}`} /></div>
                  )}
                  {p.locked && (
                    <Badge
                      className="absolute left-2 top-2 z-10 gap-1 border-0 bg-amber-500 text-black shadow-lg"
                      data-testid={`locked-poster-badge-${p.id}`}
                    >
                      <Lock className="h-3 w-3" /> Verrouillée
                    </Badge>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="truncate font-display text-base font-semibold">{p.name}</h3>
                  <p className="font-mono-label text-[10px] uppercase tracking-widest text-muted-foreground">
                    {(p.columns?.reduce((n, c) => n + (c.rows?.length || 0), 0) || 0) + (p.footer?.length || 0)} lignes
                  </p>
                  <div className="mt-4">
                    <LiveToggle poster={p} online={live.poster_ids.includes(p.id)} onOn={addToBanner} onOff={takeOffline} />
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <Button size="sm" className="flex-1" onClick={() => navigate(`/poster/${p.id}`)} data-testid={`edit-poster-${p.id}`}>
                      <Pencil className="mr-1 h-3.5 w-3.5" /> Éditer
                    </Button>
                    <Button size="icon" variant="secondary" onClick={() => duplicate(p)} data-testid={`duplicate-poster-${p.id}`} title="Dupliquer">
                      <Copy className="h-4 w-4" />
                    </Button>
                    <SendPosterButton poster={p} iconOnly />
                    {p.layout === "usc" && !p.split_of && <Button size="icon" variant="secondary" onClick={() => split(p)} disabled={!!splitting} data-testid={`split-poster-${p.id}`} title="Séparer Académie / Seniors">
                      {splitting === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Split className="h-4 w-4" />}
                    </Button>}
                    <ProtectedDelete live={live.poster_ids.includes(p.id)} kind="affiche" name={p.name} onConfirm={() => remove(p.id)}
                      triggerTestId={`delete-poster-${p.id}`} confirmTestId={`confirm-delete-poster-${p.id}`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
